/**
 * PATH: apps/hr/src/lib/useWorkerCategory.ts
 * FILE_ID: HR-LIB-WORKER-CATEGORY-001
 * PURPOSE: Read worker_category from /users/me — same logic as
 *          apps/workforce. Duplicated rather than shared because
 *          the two apps wrap the workforce-api with different
 *          client modules, and packaging the hook would force
 *          a transport adapter we don't need today.
 *
 * WHY: ClockPage uses this to skip the geolocation prompt for
 *      OFFICE/MANAGEMENT workers. The BE stamps `office_staff`
 *      verdict + 1.0 confidence regardless of GPS, so asking the
 *      browser for permission would be useless theatre.
 */
import { useEffect, useState } from 'react'
import { workforceApi } from './workforceApi'

export type WorkerCategory = 'field' | 'office' | 'subcontractor' | 'management' | null

interface UserMeResponse {
  worker_category?: string | null
}

const OFFICE_CATEGORIES = new Set(['office', 'management'])

export function useWorkerCategory(): {
  category: WorkerCategory
  isOfficeStaff: boolean
  loading: boolean
} {
  const [category, setCategory] = useState<WorkerCategory>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    workforceApi
      .get<UserMeResponse>('/users/me')
      .then((data) => {
        if (cancelled) return
        const c = (data?.worker_category || null) as WorkerCategory
        setCategory(c)
      })
      .catch(() => {
        if (cancelled) return
        setCategory(null)
      })
      .finally(() => {
        if (cancelled) return
        setLoading(false)
      })
    return () => { cancelled = true }
  }, [])

  return {
    category,
    isOfficeStaff: category != null && OFFICE_CATEGORIES.has(category),
    loading,
  }
}
